import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '@/i18n';

// Get initial language from localStorage or browser
const getInitialLanguage = () => {
  const stored = localStorage.getItem('i18nextLng');
  if (stored && ['en', 'ar'].includes(stored)) {
    return stored;
  }
  return 'en';
};

const applyLanguage = (lang) => {
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  localStorage.setItem('i18nextLng', lang);
  document.documentElement.dir = dir;
  document.documentElement.lang = lang;
};

// Initialize document direction on load
const initLang = getInitialLanguage();
applyLanguage(initLang);

const initialState = {
  current: initLang,
  direction: initLang === 'ar' ? 'rtl' : 'ltr',
  isRTL: initLang === 'ar',
  loading: false,
};

// Async thunk that waits for i18next to finish loading resources before updating state
export const setLanguage = createAsyncThunk(
  'language/setLanguage',
  async (newLanguage) => {
    await i18n.changeLanguage(newLanguage);
    applyLanguage(newLanguage);
    return newLanguage;
  }
);

export const toggleLanguage = createAsyncThunk(
  'language/toggleLanguage',
  async (_, { getState }) => {
    const current = getState().language.current;
    const newLanguage = current === 'en' ? 'ar' : 'en';
    await i18n.changeLanguage(newLanguage);
    applyLanguage(newLanguage);
    return newLanguage;
  }
);

const applyLangState = (state, lang) => {
  state.current = lang;
  state.direction = lang === 'ar' ? 'rtl' : 'ltr';
  state.isRTL = lang === 'ar';
  state.loading = false;
};

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setLanguage.pending, (state) => { state.loading = true; })
      .addCase(setLanguage.fulfilled, (state, action) => { applyLangState(state, action.payload); })
      .addCase(setLanguage.rejected, (state) => { state.loading = false; })
      .addCase(toggleLanguage.pending, (state) => { state.loading = true; })
      .addCase(toggleLanguage.fulfilled, (state, action) => { applyLangState(state, action.payload); })
      .addCase(toggleLanguage.rejected, (state) => { state.loading = false; });
  },
});

export default languageSlice.reducer;
